import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  resource,
  signal,
  viewChild,
} from '@angular/core';
import { catchError, firstValueFrom, of } from 'rxjs';
import {
  DisarmSiteConfirm,
  EventqBooleanToggle,
  EventqSiteSelectionPopover,
  EventqSiteStatFlag,
  EventqSitesStatusListPopover,
  EventqDisarmSitePopover,
  EventqToastService,
  SitesStatusListSite,
  SiteDisarmFixedDurationMinutes,
  SiteDisarmForOption,
  createDefaultSiteDisarmUntilAt,
  resolveDisplayedDisarmFixedDurationMinutes,
} from '@ecam/eventq-components';
import {
  EventsActionType,
  SitesStatusListPopoverType,
} from '../../../../core/models/browser-tab.models';
import { AgentAccessService } from '../../../../core/services/agent-access.service';
import { AuthSessionService } from '../../../../core/services/auth-session.service';
import { AgentDataService } from '../../../../core/services/agent-data.service';
import { AgentDataSyncService } from '../../../../core/services/agent-data-sync.service';
import { BrowserTabNavigationService } from '../../../../core/services/browser-tab-navigation.service';
import { BrowserTabService } from '../../../../core/services/browser-tab.service';
import { EventHandleService } from '../../../../core/services/event-handle.service';
import type { ClaimEventHandleResult } from '../../../../core/models/entities/event-handle.model';
import { isEventsLandingRoutePath } from '../../../../core/routing/browser-tab-routes';
import { autoReloadResource } from '../../../../core/utils/auto-reload-resource';
import { withPreviousResourceValue } from '../../../../core/utils/with-previous-resource-value';
import { EventActionPanel } from '../../components/event-action-panel/event-action-panel';
import { EventsInProcessSection } from '../../components/events-in-process-section/events-in-process-section';
import { EventsQueueSection } from '../../components/events-queue-section/events-queue-section';
import { ParkedEventsSection } from '../../components/parked-events-section/parked-events-section';
import { PatrolListSection } from '../../components/patrol-list-section/patrol-list-section';
import { EventsDashboardService } from '../../services/events-dashboard.service';

/** Armed list, disarmed list, and header counts. */
const SITE_STATUS_POLL_MS = 15_000;

@Component({
  selector: 'app-events-dashboard',
  imports: [
    EventqBooleanToggle,
    EventqSiteStatFlag,
    EventqSiteSelectionPopover,
    EventqSitesStatusListPopover,
    EventqDisarmSitePopover,
    EventsQueueSection,
    PatrolListSection,
    EventsInProcessSection,
    ParkedEventsSection,
    EventActionPanel,
  ],
  templateUrl: './events-dashboard.html',
  styleUrl: './events-dashboard.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-tab-expanded]': 'browserTabs.expanded()',
  },
})
export class EventsDashboard {
  private readonly eventsService = inject(EventsDashboardService);
  private readonly agentData = inject(AgentDataService);
  private readonly dataSync = inject(AgentDataSyncService);
  private readonly tabNavigation = inject(BrowserTabNavigationService);
  private readonly eventHandles = inject(EventHandleService);
  private readonly toast = inject(EventqToastService);
  private readonly agentAccess = inject(AgentAccessService);
  private readonly auth = inject(AuthSessionService);
  readonly browserTabs = inject(BrowserTabService);

  constructor() {
    this.dataSync.watchWhileAlive('sites');
    autoReloadResource(this.siteStatsSource, SITE_STATUS_POLL_MS);
    autoReloadResource(this.siteArmedSource, SITE_STATUS_POLL_MS);
    autoReloadResource(this.siteDisarmedSource, SITE_STATUS_POLL_MS);
  }

  private readonly eventsQueueSection = viewChild(EventsQueueSection);
  private readonly armedSitesTrigger = viewChild<EventqSiteStatFlag>('armedSitesTrigger');
  private readonly disarmedSitesTrigger = viewChild<EventqSiteStatFlag>('disarmedSitesTrigger');

  readonly showEventQueue = this.browserTabs.showEventQueue;
  readonly eventsActionPopover = this.browserTabs.eventsActionPopover;
  readonly eventsActionSelectedSiteId = this.browserTabs.eventsActionSelectedSiteId;
  readonly eventsActionSearchQuery = this.browserTabs.eventsActionSearchQuery;
  readonly sitesStatusListPopover = this.browserTabs.sitesStatusListPopover;
  readonly sitesStatusListSearchQuery = this.browserTabs.sitesStatusListSearchQuery;
  readonly siteArmDisarmPopover = this.browserTabs.siteArmDisarmPopover;
  readonly siteArmDisarmSiteId = this.browserTabs.siteArmDisarmSiteId;
  readonly siteDisarmReasonId = this.browserTabs.siteDisarmReasonId;
  readonly siteDisarmFor = this.browserTabs.siteDisarmFor;
  readonly siteDisarmFixedDurationMinutes = this.browserTabs.siteDisarmFixedDurationMinutes;
  readonly siteDisarmFixedDurationCustom = this.browserTabs.siteDisarmFixedDurationCustom;
  readonly siteDisarmFixedDurationCustomDraft = this.browserTabs.siteDisarmFixedDurationCustomDraft;
  readonly siteDisarmFixedDurationMinutesForUi = computed(() =>
    resolveDisplayedDisarmFixedDurationMinutes(
      this.siteDisarmFixedDurationMinutes(),
      this.siteDisarmFixedDurationCustom(),
    ),
  );
  readonly siteDisarmUntilAt = this.browserTabs.siteDisarmUntilAt;
  readonly canArmDisarmSite = this.agentAccess.canArmDisarmSite;
  readonly canSelectStatusListSite = computed(
    () => this.canArmDisarmSite() && this.sitesStatusListPopover() === 'armed',
  );
  readonly canPark = this.agentAccess.canPark;
  readonly canShowEventQueueToggle = this.agentAccess.canShowEventQueueToggle;
  /** Escalation keeps the site flags and does not see queue, patrol, or actions. */
  readonly showOperatorWorkspace = computed(() => this.auth.role() !== 'Escalation');

  readonly armDisarmSubmitting = signal(false);
  /** Blocks a second row claim on the same tab while one claim is in flight. */
  private readonly claimingTabIds = new Set<string>();
  /** One "Unable to open event" toast per event id (service may already have marked unopenable). */
  private readonly toastedClaimFailureEventIds = new Set<string>();

  private readonly siteStatsSource = resource({
    params: () => ({ sync: this.dataSync.revisionFor('sites') }),
    loader: () => firstValueFrom(this.eventsService.getSiteStats()),
  });
  readonly siteStats = withPreviousResourceValue(this.siteStatsSource);

  readonly siteLocationsResource = withPreviousResourceValue(
    resource({
      params: () => ({ sync: this.dataSync.revisionFor('sites') }),
      loader: () => firstValueFrom(this.agentData.getSiteLocations()),
    }),
  );

  private readonly siteArmedSource = resource({
    params: () => ({ sync: this.dataSync.revisionFor('sites') }),
    loader: () => firstValueFrom(this.agentData.getArmedSites()),
  });
  readonly siteArmedResource = withPreviousResourceValue(this.siteArmedSource);

  private readonly siteDisarmedSource = resource({
    params: () => ({ sync: this.dataSync.revisionFor('sites') }),
    loader: () => firstValueFrom(this.agentData.getDisarmedSites()),
  });
  readonly siteDisarmedResource = withPreviousResourceValue(this.siteDisarmedSource);

  readonly disarmReasonsResource = resource({
    params: () => ({
      open: this.siteArmDisarmPopover() === 'disarm',
    }),
    loader: async ({ params }) => {
      if (!params.open) {
        return { items: [] };
      }
      return firstValueFrom(this.agentData.getSiteDisarmReasons());
    },
  });

  /** IANA zone for the disarm calendar. Empty until the dealer site record loads. */
  readonly disarmSiteTimeZoneResource = resource({
    params: () => {
      const siteId = this.siteArmDisarmSiteId();
      if (this.siteArmDisarmPopover() !== 'disarm' || !siteId) {
        return undefined;
      }
      return { siteId };
    },
    loader: ({ params }) => {
      const siteId = Number(params.siteId);
      if (!Number.isFinite(siteId)) {
        return Promise.resolve('');
      }
      return firstValueFrom(
        this.agentData.getDealerSite(siteId).pipe(
          catchError(() => of(null)),
        ),
      ).then((site) => site?.timezone?.trim() || 'UTC');
    },
  });

  readonly disarmSiteTimeZone = computed(() => this.disarmSiteTimeZoneResource.value() ?? '');

  readonly actionSitesLoading = computed(
    () => this.siteLocationsResource.isLoading() && !this.siteLocationsResource.hasValue(),
  );

  readonly filteredSites = computed(() => {
    const items = this.siteLocationsResource.value()?.items ?? [];
    const query = this.eventsActionSearchQuery().trim().toLowerCase();
    if (!query) {
      return items;
    }

    return items.filter(
      (site) =>
        site.name.toLowerCase().includes(query) || site.address.toLowerCase().includes(query),
    );
  });

  readonly statusListSites = computed((): SitesStatusListSite[] => {
    const variant = this.sitesStatusListPopover();
    if (!variant) {
      return [];
    }

    const items =
      variant === 'armed'
        ? (this.siteArmedResource.value()?.items ?? [])
        : (this.siteDisarmedResource.value()?.items ?? []);
    const query = this.sitesStatusListSearchQuery().trim().toLowerCase();
    if (!query) {
      return items;
    }

    return items.filter(
      (site) =>
        site.name.toLowerCase().includes(query) || site.address.toLowerCase().includes(query),
    );
  });

  readonly statusListOpen = computed(() => this.sitesStatusListPopover() !== null);

  readonly statusListAnchor = computed(() => {
    const variant = this.sitesStatusListPopover();
    if (variant === 'armed') {
      return this.armedSitesTrigger()?.hostElement ?? null;
    }
    if (variant === 'disarmed') {
      return this.disarmedSitesTrigger()?.hostElement ?? null;
    }
    return null;
  });

  readonly disarmPopoverAnchor = computed(() => this.armedSitesTrigger()?.hostElement ?? null);

  readonly selectedArmDisarmSite = computed(() => {
    const siteId = this.siteArmDisarmSiteId();
    if (!siteId) {
      return null;
    }
    return (
      (this.siteArmedResource.value()?.items ?? []).find((site) => site.id === siteId) ??
      (this.siteDisarmedResource.value()?.items ?? []).find((site) => site.id === siteId) ??
      (this.siteLocationsResource.value()?.items ?? []).find((site) => site.id === siteId) ??
      null
    );
  });

  readonly disarmPopoverOpen = computed(() => this.siteArmDisarmPopover() === 'disarm');

  readonly popoverOpen = computed(() => this.eventsActionPopover() !== null);

  readonly popoverTitle = computed(() =>
    this.eventsActionPopover() === 'site-check' ? 'Site Check' : 'Manual Patrol',
  );

  readonly popoverConfirmLabel = computed(() =>
    this.eventsActionPopover() === 'site-check' ? 'Start Site Check' : 'Start Manual Patrol',
  );

  readonly queueCount = computed(() => this.eventsQueueSection()?.totalCount() ?? 0);

  setShowEventQueue(checked: boolean): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    this.browserTabs.updateActivePageState({ showEventQueue: checked });
  }

  onProcessEvent(): void {
    void this.startProcessEvent();
  }

  onQueueEventActivate(eventId: string): void {
    void this.startClaimedEvent(eventId);
  }

  onParkedEventActivate(eventId: string): void {
    void this.startClaimedEvent(eventId);
  }

  private async startClaimedEvent(eventId: string): Promise<void> {
    if (!eventId || this.browserTabs.claimInteractionLocked()) {
      return;
    }

    this.clearStaleActiveEventOnLanding();

    const tabId = this.browserTabs.activeTabId();
    if (!tabId) {
      return;
    }

    const state = this.browserTabs.activePageState();
    if (
      state.activeEventId === eventId &&
      (state.eventHandleId || !isEventsLandingRoutePath(state.routePath))
    ) {
      await this.tabNavigation.openClaimedProcessEvent(eventId);
      return;
    }

    if (!this.eventHandles.tryBeginOpen(eventId)) {
      return;
    }

    if (this.claimingTabIds.has(tabId)) {
      this.eventHandles.releaseOpenAttempt(eventId);
      return;
    }

    if (!this.browserTabs.canOpenMoreWork() && !state.activeEventId) {
      this.eventHandles.releaseOpenAttempt(eventId);
      this.toast.show('notice', {
        title: 'Open work limit reached',
        subtitle: 'Close or complete a workspace before claiming another event.',
        tone: 'error',
      });
      return;
    }

    if (this.browserTabs.activePageState().activeEventId) {
      this.eventHandles.releaseOpenAttempt(eventId);
      this.toast.show('notice', {
        title: 'Event already open',
        subtitle: 'This tab already has an event. Open a new tab to process another.',
        tone: 'error',
      });
      return;
    }

    this.claimingTabIds.add(tabId);
    const endClaim = this.browserTabs.beginClaimInteraction(tabId);
    let claimed: ClaimEventHandleResult | null | undefined;
    try {
      claimed = await this.eventHandles.claimEventForTab(tabId, eventId);
      this.showClaimFailureToast(eventId, claimed);
      if (!claimed?.ok) {
        return;
      }

      await this.tabNavigation.openClaimedProcessEvent(claimed.handle.eventId);
    } catch {
      claimed = null;
      this.showClaimFailureToast(eventId, null);
    } finally {
      this.claimingTabIds.delete(tabId);
      endClaim();
      this.eventHandles.releaseOpenAttempt(eventId);
    }
  }

  private showClaimFailureToast(
    eventId: string,
    claimed: ClaimEventHandleResult | null | undefined,
  ): void {
    if (claimed?.ok) {
      return;
    }

    const workLimit = claimed && !claimed.ok && claimed.conflict.code === 'WORK_LIMIT';
    if (!workLimit) {
      this.eventHandles.markEventUnopenable(eventId);
      if (this.toastedClaimFailureEventIds.has(eventId)) {
        return;
      }
      this.toastedClaimFailureEventIds.add(eventId);
    }

    this.toast.show('notice', {
      title: workLimit ? 'Open work limit reached' : 'Unable to open event',
      subtitle:
        claimed && !claimed.ok
          ? claimed.conflict.message
          : 'This event is unavailable or already held by another agent.',
      tone: 'error',
    });
  }

  private async startProcessEvent(): Promise<void> {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }

    this.clearStaleActiveEventOnLanding();

    if (this.browserTabs.activePageState().activeEventId) {
      this.toast.show('notice', {
        title: 'Event already open',
        subtitle: 'This tab already has an event. Open a new tab to process another.',
        tone: 'error',
      });
      return;
    }

    if (!this.browserTabs.canOpenMoreWork()) {
      this.toast.show('notice', {
        title: 'Open work limit reached',
        subtitle: 'Close or complete a workspace before claiming another event.',
        tone: 'error',
      });
      return;
    }

    const tabId = this.browserTabs.activeTabId();
    if (!tabId) {
      return;
    }

    const endClaim = this.browserTabs.beginClaimInteraction(tabId);
    try {
      const claimed = await this.eventHandles.claimNextForTab(tabId);
      if (!claimed?.ok) {
        const workLimit = claimed && !claimed.ok && claimed.conflict.code === 'WORK_LIMIT';
        this.toast.show('notice', {
          title: workLimit ? 'Open work limit reached' : 'No available events',
          subtitle: workLimit
            ? claimed.conflict.message
            : 'Every waiting event is already held by another agent or tab.',
          tone: 'error',
        });
        return;
      }

      await this.tabNavigation.openClaimedProcessEvent(claimed.handle.eventId);
    } finally {
      endClaim();
    }
  }

  /**
   * Landing route may retain a stale activeEventId after server reset / interrupted load
   * without a live handle — clear it so claim is not blocked by a false "already open".
   */
  private clearStaleActiveEventOnLanding(): void {
    const state = this.browserTabs.activePageState();
    if (
      state.activeEventId &&
      !state.eventHandleId &&
      isEventsLandingRoutePath(state.routePath)
    ) {
      this.browserTabs.updateActivePageState({ activeEventId: null });
    }
  }

  onManualPatrol(): void {
    this.openActionPopover('manual-patrol');
  }

  onSiteCheck(): void {
    this.openActionPopover('site-check');
  }

  onPopoverClose(): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    this.browserTabs.updateActivePageState({
      eventsActionPopover: null,
      eventsActionSelectedSiteId: null,
      eventsActionSearchQuery: '',
      eventsActionOccupancySiteIds: null,
    });
  }

  onSearchQueryChange(query: string): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    this.browserTabs.updateActivePageState({ eventsActionSearchQuery: query });
  }

  onSelectedSiteChange(siteId: string): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    this.browserTabs.updateActivePageState({ eventsActionSelectedSiteId: siteId });
  }

  onPopoverConfirm(event: { siteId: string }): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    const actionType = this.eventsActionPopover();
    if (!actionType) {
      return;
    }

    this.browserTabs.updateActivePageState({
      pendingAction: { type: actionType, siteId: event.siteId },
      eventsActionPopover: null,
      eventsActionSelectedSiteId: null,
      eventsActionSearchQuery: '',
      eventsActionOccupancySiteIds: null,
    });

    void this.tabNavigation.navigateWithinActiveTab('load', actionType);
  }

  openSitesStatusList(variant: Exclude<SitesStatusListPopoverType, null>): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    this.browserTabs.updateActivePageState({
      sitesStatusListPopover: variant,
      sitesStatusListSearchQuery: '',
      siteArmDisarmPopover: null,
      siteArmDisarmSiteId: null,
      siteDisarmReasonId: null,
      siteDisarmFor: 'until-next-schedule',
      siteDisarmFixedDurationMinutes: null,
      siteDisarmFixedDurationCustom: false,
      siteDisarmFixedDurationCustomDraft: '',
      siteDisarmUntilAt: null,
    });
  }

  onSitesStatusListClose(): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    this.browserTabs.updateActivePageState({
      sitesStatusListPopover: null,
      sitesStatusListSearchQuery: '',
      siteArmDisarmPopover: null,
      siteArmDisarmSiteId: null,
      siteDisarmReasonId: null,
      siteDisarmFor: 'until-next-schedule',
      siteDisarmFixedDurationMinutes: null,
      siteDisarmFixedDurationCustom: false,
      siteDisarmFixedDurationCustomDraft: '',
      siteDisarmUntilAt: null,
    });
  }

  onSitesStatusListSearchChange(query: string): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    this.browserTabs.updateActivePageState({ sitesStatusListSearchQuery: query });
  }

  onSitesStatusSiteSelect(site: SitesStatusListSite): void {
    if (this.browserTabs.claimInteractionLocked() || !this.canArmDisarmSite()) {
      return;
    }

    const listVariant = this.sitesStatusListPopover();
    if (listVariant !== 'armed') {
      return;
    }

    this.browserTabs.updateActivePageState({
      sitesStatusListPopover: null,
      sitesStatusListSearchQuery: '',
      siteArmDisarmPopover: 'disarm',
      siteArmDisarmSiteId: site.id,
      siteDisarmReasonId: null,
      siteDisarmFor: 'until-next-schedule',
      siteDisarmFixedDurationMinutes: null,
      siteDisarmFixedDurationCustom: false,
      siteDisarmFixedDurationCustomDraft: '',
      siteDisarmUntilAt: null,
    });
  }

  onArmDisarmClose(): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    this.browserTabs.updateActivePageState({
      siteArmDisarmPopover: null,
      siteArmDisarmSiteId: null,
      siteDisarmReasonId: null,
      siteDisarmFor: 'until-next-schedule',
      siteDisarmFixedDurationMinutes: null,
      siteDisarmFixedDurationCustom: false,
      siteDisarmFixedDurationCustomDraft: '',
      siteDisarmUntilAt: null,
    });
  }

  onDisarmReasonChange(reasonId: string | null): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    this.browserTabs.updateActivePageState({ siteDisarmReasonId: reasonId });
  }

  onDisarmForChange(disarmFor: SiteDisarmForOption): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    const keepFixed = disarmFor === 'fixed-time';
    this.browserTabs.updateActivePageState({
      siteDisarmFor: disarmFor,
      siteDisarmFixedDurationMinutes: keepFixed ? this.siteDisarmFixedDurationMinutes() : null,
      siteDisarmFixedDurationCustom: keepFixed ? this.siteDisarmFixedDurationCustom() : false,
      siteDisarmFixedDurationCustomDraft: keepFixed
        ? this.siteDisarmFixedDurationCustomDraft()
        : '',
      siteDisarmUntilAt: disarmFor === 'date-time' ? createDefaultSiteDisarmUntilAt() : null,
    });
  }

  onDisarmFixedDurationChange(minutes: SiteDisarmFixedDurationMinutes | null): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    this.browserTabs.updateActivePageState({ siteDisarmFixedDurationMinutes: minutes });
  }

  onDisarmFixedDurationCustomChange(custom: boolean): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    this.browserTabs.updateActivePageState({ siteDisarmFixedDurationCustom: custom });
  }

  onDisarmFixedDurationCustomDraftChange(draft: string): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    this.browserTabs.updateActivePageState({ siteDisarmFixedDurationCustomDraft: draft });
  }

  onDisarmUntilAtChange(untilAt: string | null): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    this.browserTabs.updateActivePageState({ siteDisarmUntilAt: untilAt });
  }

  onDisarmConfirm(event: DisarmSiteConfirm): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    void this.submitDisarmSite(event);
  }

  private async submitDisarmSite(event: DisarmSiteConfirm): Promise<void> {
    const site = this.selectedArmDisarmSite();
    if (!site || this.armDisarmSubmitting()) {
      return;
    }

    this.armDisarmSubmitting.set(true);
    try {
      await firstValueFrom(
        this.agentData.disarmSite(site.id, {
          reasonId: event.reasonId,
          disarmFor: event.disarmFor,
          ...(event.durationMinutes != null ? { durationMinutes: event.durationMinutes } : {}),
          ...(event.untilAt != null ? { untilAt: event.untilAt } : {}),
        }),
      );
      this.toast.show('site-disarmed', {
        subtitle: `${site.name} disarm was submitted. It can take up to a minute to appear in Disarmed Sites.`,
        iconSrc: '/assets/events/icons/toast/site-disarmed.svg',
      });
      this.onArmDisarmClose();
    } catch (error) {
      this.handleDisarmError(error);
    } finally {
      this.armDisarmSubmitting.set(false);
    }
  }

  private handleDisarmError(error: unknown): void {
    let message = 'Unable to disarm site. Please try again.';

    if (error instanceof HttpErrorResponse) {
      if (error.status === 403) {
        message = 'You do not have permission to disarm sites.';
      } else if (typeof error.error === 'string' && error.error.trim()) {
        message = error.error.trim();
      }
    }

    this.toast.show('notice', {
      title: 'Disarm failed',
      subtitle: message,
      tone: 'error',
    });
  }

  private openActionPopover(type: EventsActionType): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    this.browserTabs.updateActivePageState({
      eventsActionPopover: type,
      eventsActionSelectedSiteId: null,
      eventsActionSearchQuery: '',
      eventsActionOccupancySiteIds: null,
    });
  }
}
